/// Premium-tier flag. Read from localStorage so the UI can gate
/// content without waiting on the worker. The flag is *set* by the
/// IAP success callback (see `nav-games-worker` integration, future)
/// and *cleared* by the auth client on sign-out so a shared device
/// doesn't leak entitlement across accounts.
///
/// During iter 4 (no IAP yet) this is inert — `isPremium()` returns
/// false, so premium-gated content stays locked. Tests can override
/// via `setPremium(true)`.

const KEY = "nav-games.tier.premium";

export function isPremium(storage: Storage = defaultStorage()): boolean {
  try {
    return storage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

export function setPremium(
  value: boolean,
  storage: Storage = defaultStorage(),
): void {
  try {
    if (value) storage.setItem(KEY, "1");
    else storage.removeItem(KEY);
  } catch {
    /* localStorage unavailable (private-mode safari, SSR) — silently no-op */
  }
}

function defaultStorage(): Storage {
  return typeof window !== "undefined" ? window.localStorage : memoryStorage();
}

/// Drop-in `Storage` for environments without `window.localStorage`
/// (Node tests, SSR). Methods match the standard `Storage` shape
/// closely enough for our usage; we don't expose this — callers use
/// `defaultStorage()` and tests pass their own.
function memoryStorage(): Storage {
  const map = new Map<string, string>();
  return {
    get length() {
      return map.size;
    },
    clear: () => map.clear(),
    getItem: (k) => map.get(k) ?? null,
    key: (i) => [...map.keys()][i] ?? null,
    removeItem: (k) => {
      map.delete(k);
    },
    setItem: (k, v) => {
      map.set(k, v);
    },
  };
}
